<template>
    <!-- Quick add: hand a free port on a chosen server to a customer (a free port is picked at random).
         mode 'normal': 1 day / 1 week / 1 month / custom.  mode 'test': a number of minutes. -->
    <div class="modal fade" id="licenseQuickAddModal" tabindex="-1" aria-hidden="true" ref="modalEl">
        <div class="modal-dialog modal-dialog-centered w-[640px] max-w-[96vw]">
            <div class="modal-content">
                <div class="modal-header">
                    <h1 class="modal-title fs-5">{{ isTest ? 'Add Test License (minutes)' : 'Add License to Customer' }}</h1>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <div class="px-2 py-2 modal-item">
                        Customer Email:
                        <v-combobox v-model="customerEmail" :items="customerList.map(c => c.email)" rounded="0"
                            variant="outlined" density="compact" :hide-no-data="false" placeholder="customer@email">
                            <template v-slot:no-data>
                                <v-list-item><v-list-item-title>
                                    <p class="text-sm text-gray-600 text-center">Not found</p>
                                </v-list-item-title></v-list-item>
                            </template>
                        </v-combobox>
                    </div>
                    <div class="px-2 py-2 modal-item">
                        <div class="flex items-center justify-between">
                            <span>Server{{ loadingPorts ? ' (refreshing…)' : '' }}:</span>
                            <!-- region filter: UK / USA / rest of the world -->
                            
                        </div>
                        <v-autocomplete v-model="selectedServer" :items="visibleServers" item-title="label" item-value="modem_name"
                            return-object rounded="0" variant="outlined" density="compact"
                            placeholder="Type a server name" no-data-text="No server has a free port"></v-autocomplete>
                    </div>
                    <!-- which free port: a random connected one (the usual), or one chosen from the list -->
                    <div v-if="selectedServer" class="px-2 py-2 modal-item">
                        <div class="flex items-center gap-3 flex-wrap">
                            <span>Port</span>
                            <div class="btn-group">
                                <button type="button" class="btn btn-sm" :class="portMode === 'random' ? 'btn-primary' : 'btn-outline-secondary'" @click="portMode = 'random'; chosenPort = null">Random free port</button>
                                <button type="button" class="btn btn-sm" :class="portMode === 'choose' ? 'btn-primary' : 'btn-outline-secondary'" @click="portMode = 'choose'">Choose a port</button>
                            </div>
                        </div>
                        <div v-if="portMode === 'choose'" class="port-pick">
                            <div class="port-pick__row port-pick__row--head">
                                <span>Port (HTTP / SOCKS5)</span><span>{{ ['PROXYSMART', 'PROPROXYUI'].includes(selectedServer.server_type) ? 'IMEI' : 'Position' }}</span><span>Status</span><span>Last used</span>
                            </div>
                            <label v-for="p in selectedServer.freePorts" :key="p.ports" class="port-pick__row" :class="{ 'is-on': chosenPort === p.ports }">
                                <span><input type="radio" name="quick-port" :value="p.ports" v-model="chosenPort" class="me-2" />{{ p.ports.replace('/', ' / ') }}</span>
                                <span>{{ p.position ? 'position ' + p.position : (p.imei || '–') }}</span>
                                <span :class="p.connected ? 'text-ok' : 'text-warn'"><v-icon icon="mdi-circle" size="9" class="me-1"></v-icon>{{ p.connected ? 'Connected' : 'Disconnected' }}</span>
                                <span class="text-gray-500">{{ p.last_used ? ago(p.last_used) : 'never' }}</span>
                            </label>
                        </div>
                    </div>
                    <!-- duration -->
                    <div v-if="isTest" class="px-2 py-2 modal-item">
                        Minutes:
                        <v-text-field v-model.number="minutes" type="number" min="1" max="1440" variant="outlined" density="compact" rounded="0"
                            hide-details class="w-40"></v-text-field>
                    </div>
                    <template v-else>
                        <div class="px-2 py-2 modal-item">
                            Duration:
                            <div class="flex gap-2 mt-1">
                                <button class="btn btn-sm" :class="pick === '1d' ? 'btn-primary' : 'btn-outline-secondary'" @click="pick = '1d'">1 day</button>
                                <button class="btn btn-sm" :class="pick === '1w' ? 'btn-primary' : 'btn-outline-secondary'" @click="pick = '1w'">1 week</button>
                                <button class="btn btn-sm" :class="pick === '1m' ? 'btn-primary' : 'btn-outline-secondary'" @click="pick = '1m'">1 month</button>
                                <button class="btn btn-sm" :class="pick === 'custom' ? 'btn-primary' : 'btn-outline-secondary'" @click="pick = 'custom'">Custom</button>
                            </div>
                        </div>
                        <div v-if="pick === 'custom'" class="px-2 py-1">
                            Expires at (date and time):
                            <VueDatePicker v-model="customDate" format="dd/MM/yyyy HH:mm" :min-date="new Date()" :clearable="false" :teleport="true"></VueDatePicker>
                        </div>
                    </template>
                    <div class="px-2 py-2 modal-item">
                        Username:password
                        <v-text-field v-model="authUser" variant="outlined" density="compact" rounded="0" hide-details>
                            <template v-slot:append-inner>
                                <v-icon icon="mdi-refresh" @click="generateRandomCredentials"></v-icon>
                            </template>
                        </v-text-field>
                    </div>
                    <!-- supplier of the chosen server: book a top-up in the same step, like a customer purchase -->
                    <div v-if="!isTest && selectedServer && supplierLoading" class="px-2 pt-2 text-xs text-gray-500">Checking the server supplier…</div>
                    <div v-else-if="!isTest && selectedServer && supplier" class="px-2 pt-2 mt-2 border-top">
                        <div class="text-sm">
                            Supplier: <b>{{ supplier.name }}</b> <span class="text-gray-500">({{ supplier.email }})</span>
                            · cost <b>{{ costLine }}</b>
                            · balance ${{ money(supplier.balance) }}
                        </div>
                        <div class="flex items-center gap-3 mt-2">
                            <label class="text-sm whitespace-nowrap">Top up supplier (USD)</label>
                            <v-text-field v-model.number="supplierAmount" type="number" min="0" step="0.01" variant="outlined" density="compact" rounded="0" hide-details class="w-40" :disabled="noSupplierCredit"></v-text-field>
                        </div>
                        <label class="flex items-center gap-2 text-sm mt-2">
                            <input type="checkbox" v-model="noSupplierCredit" class="form-check-input shadow-none" style="height: 18px; width: 18px;" />
                            Don't credit the supplier
                        </label>
                    </div>

                    <!-- charge the customer: off by default; the standard price for the period, editable -->
                    <div v-if="selectedServer" class="px-2 pt-2 mt-2 border-top">
                        <label class="flex items-center gap-2 text-sm">
                            <input type="checkbox" v-model="chargeCustomer" class="form-check-input shadow-none" style="height: 18px; width: 18px;" />
                            Deduct from the customer's balance
                            <span class="text-gray-500"><template v-if="customerInfo"> · balance ${{ money(customerInfo.balance) }}</template><template v-if="priceLine"> · price {{ priceLine }}</template></span>
                        </label>
                        <template v-if="chargeCustomer">
                            <div class="flex items-center gap-3 mt-2">
                                <label class="text-sm whitespace-nowrap">Charge customer (USD)</label>
                                <v-text-field v-model.number="customerAmount" type="number" min="0" step="0.01" variant="outlined" density="compact" rounded="0" hide-details class="w-40"></v-text-field>
                                <span v-if="customerInfo && Number(customerAmount) > Number(customerInfo.balance)" class="text-xs text-red-500">More than the customer's balance; the server will refuse.</span>
                            </div>
                        </template>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                    <button type="button" :disabled="busy || !customerEmail || !selectedServer || !amount || (isCustom && !customDate)" class="btn btn-primary"
                        @click="create">{{ busy ? 'Adding…' : (isTest ? 'Add Test License' : 'Add License') + (chargeCustomer && Number(customerAmount) > 0 ? ' − $' + money(customerAmount) + ' from customer' : '') + (!isTest && supplier && !noSupplierCredit && Number(supplierAmount) > 0 ? ' + $' + money(supplierAmount) + ' to ' + supplier.name : '') }}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, defineProps, defineEmits } from 'vue';
import { useAdminLicenseStore, useAlertStore } from "@/stores"
import VueDatePicker from '@vuepic/vue-datepicker';

const props = defineProps({
    customerEmail: { type: String, default: '' },   // the email typed in the page filter, if any
    mode: { type: String, default: 'normal' },      // 'normal' | 'test'
});
const emit = defineEmits(['updated']);
const store = useAdminLicenseStore();

const modalEl = ref(null);
const customerList = ref([]);
const ports = ref([]);              // every free port, from the server
const loadingPorts = ref(false);
const customerEmail = ref('');
const selectedServer = ref(null);
const authUser = ref('');
const busy = ref(false);
const isTest = computed(() => props.mode === 'test');

// supplier of the chosen server (from its packages) + the top-up to book with the licence
const supplier = ref(undefined);        // undefined = not looked up yet, null = none
const supplierLoading = ref(false);
const supplierAmount = ref(0);
const prices = ref({});               // selling price per period on the chosen server
const customerInfo = ref(null);       // { email, balance } of the typed customer, when known
const chargeCustomer = ref(false);
const noSupplierCredit = ref(false);   // tick box: no top-up and no transaction for the supplier
const customerAmount = ref(0);
const costFor = (unitKey) => Number((supplier.value?.costs || {})[unitKey] ?? supplier.value?.cost_price ?? 0);
const money = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const periodWord = (u) => ({ DAY: 'day', WEEK: 'week', MONTH: 'month' }[String(u || '').toUpperCase()] || 'period');
const costLine = computed(() => {
    const c = supplier.value?.costs || {};
    const parts = ['DAY', 'WEEK', 'MONTH'].filter(k => c[k] != null).map(k => '$' + money(c[k]) + '/' + periodWord(k) + (supplier.value?.custom?.[k] ? ' (custom commission)' : ''));
    return parts.length ? parts.join(' · ') : '$' + money(supplier.value?.cost_price);
});

// One row per server that has at least one free port, with the count.
// region filter buttons
const regions = [{ key: 'uk', label: 'UK' }, { key: 'usa', label: 'USA' }, { key: 'rest', label: 'Others' }];
const region = ref('all')   // one country: every server;
const regionOf = (country) => {
    const c = String(country || '').toLowerCase();
    if (c.includes('united kingdom') || c === 'uk' || c.includes('great britain') || c.includes('england')) return 'uk';
    if (c.includes('united states') || c === 'usa' || c === 'us' || c.includes('america')) return 'usa';
    return 'rest';
};
const servers = computed(() => {
    const byServer = new Map();
    for (const p of ports.value) {
        if (!byServer.has(p.modem_name)) {
            byServer.set(p.modem_name, { modem_name: p.modem_name, server_type: p.server_type, city: p.city, isp: p.isp, country: p.country, free: [], freePorts: [] });
        }
        byServer.get(p.modem_name).freePorts.push(p);
        if (p.connected !== false) byServer.get(p.modem_name).free.push(p.ports);   // the random pick only uses connected ports
    }
    return [...byServer.values()]
        .filter(s => s.freePorts.length)
        .map(s => ({ ...s, count: s.free.length, label: s.modem_name + ' - (' + s.free.length + ' available' + (s.freePorts.length > s.free.length ? ', ' + (s.freePorts.length - s.free.length) + ' offline' : '') + ')' }))
        .sort((a, b) => a.modem_name.localeCompare(b.modem_name));
});
// port choice: 'random' (a connected free port, as before) or 'choose' (a specific one from the list)
const portMode = ref('random');
const chosenPort = ref(null);
watch(selectedServer, () => { chosenPort.value = null; });
const chosenPortInfo = computed(() => { const p = selectedServer.value?.freePorts?.find(x => x.ports === chosenPort.value); return p ? (p.position ? ' (position ' + p.position + ')' : p.imei ? ' (IMEI ' + p.imei + ')' : '') : ''; });
const ago = (v) => { const d = Math.round((Date.now() - new Date(v).getTime()) / 864e5); return d <= 0 ? 'today' : d === 1 ? 'yesterday' : d < 60 ? d + ' days ago' : Math.round(d / 30) + ' months ago'; };
const visibleServers = computed(() => servers.value.filter(s => region.value === 'all' || regionOf(s.country) === region.value));
const countFor = (key) => servers.value.filter(s => regionOf(s.country) === key).length;

// normal mode
const pick = ref('1d');
const inOneDay = () => new Date(Date.now() + 24 * 60 * 60 * 1000);
const customDate = ref(inOneDay());   // Custom: exact expiry from the calendar
// test mode
const minutes = ref(65);

const isCustom = computed(() => !isTest.value && pick.value === 'custom');
const amount = computed(() => isTest.value ? (Number(minutes.value) || 0) : 1);
const unit = computed(() => isTest.value ? 'minutes' : pick.value === '1d' ? 'days' : pick.value === '1w' ? 'weeks' : 'months');
const expiryPreview = computed(() => {
    if (isCustom.value) return customDate.value ? new Date(customDate.value).toLocaleString() : '-';
    const d = new Date(); const n = amount.value;
    if (unit.value === 'minutes') d.setMinutes(d.getMinutes() + n);
    else if (unit.value === 'hours') d.setHours(d.getHours() + n);
    else if (unit.value === 'days') d.setDate(d.getDate() + n);
    else if (unit.value === 'weeks') d.setDate(d.getDate() + 7 * n);
    else d.setMonth(d.getMonth() + n);
    return d.toLocaleString();
});

// suggested top-up: the supplier's package cost for the period picked (custom = daily cost x days); tests pre-fill 0
const suggested = computed(() => {
    if (!supplier.value || isTest.value) return 0;
    if (pick.value === '1d') return costFor('DAY');
    if (pick.value === '1w') return costFor('WEEK');
    if (pick.value === '1m') return costFor('MONTH');
    if (!customDate.value) return 0;
    const days = Math.max(0, Math.round((new Date(customDate.value).getTime() - Date.now()) / 86400000));
    return Math.round(costFor('DAY') * days * 100) / 100;
});
watch(suggested, (v) => { supplierAmount.value = v; });
// customer side: the standard selling price for the period picked (custom = daily x days); tests pre-fill 0
const priceFor = (unitKey) => Number((prices.value || {})[unitKey] ?? 0);
const suggestedCustomer = computed(() => {
    if (isTest.value) return 0;
    if (pick.value === '1d') return priceFor('DAY');
    if (pick.value === '1w') return priceFor('WEEK');
    if (pick.value === '1m') return priceFor('MONTH');
    if (!customDate.value) return 0;
    const days = Math.max(0, Math.round((new Date(customDate.value).getTime() - Date.now()) / 86400000));
    return Math.round(priceFor('DAY') * days * 100) / 100;
});
watch(suggestedCustomer, (v) => { customerAmount.value = v; });
watch(chargeCustomer, (on) => { if (on) customerAmount.value = suggestedCustomer.value; });
const priceLine = computed(() => ['DAY', 'WEEK', 'MONTH'].filter(k => (prices.value || {})[k] != null).map(k => '$' + money(prices.value[k]) + '/' + periodWord(k)).join(' · '));
const loadServerInfo = async () => {
    const s = selectedServer.value;
    supplier.value = undefined; supplierAmount.value = 0; noSupplierCredit.value = false;
    if (!s) { prices.value = {}; customerInfo.value = null; return; }
    supplierLoading.value = true;
    const info = await store.serverInfo(s.modem_name, String(customerEmail.value || '').trim());
    if (selectedServer.value?.modem_name !== s.modem_name) return;   // user moved on while loading
    supplier.value = info.supplier; prices.value = info.prices; customerInfo.value = info.customer;
    supplierLoading.value = false;
    supplierAmount.value = suggested.value;
    customerAmount.value = suggestedCustomer.value;
};
watch(selectedServer, loadServerInfo);
watch(customerEmail, async () => {   // balance of the customer being typed / picked
    if (!selectedServer.value) return;
    const info = await store.serverInfo(selectedServer.value.modem_name, String(customerEmail.value || '').trim());
    customerInfo.value = info.customer;
});

function generateRandomCredentials() {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let u = '', p = '';
    for (let i = 0; i < 7; i++) u += chars.charAt(Math.floor(Math.random() * chars.length));
    for (let i = 0; i < 7; i++) p += Math.floor(Math.random() * 10);
    authUser.value = 'u' + u + ':p' + p;
}

// Fresh data every time the dialog opens: free ports change constantly.
async function refresh() {
    customerEmail.value = props.customerEmail && props.customerEmail !== 'All' ? props.customerEmail : '';
    selectedServer.value = null; supplier.value = undefined; supplierAmount.value = 0;
    chargeCustomer.value = false; customerAmount.value = 0; customerInfo.value = null; prices.value = {};
    pick.value = '1d'; customDate.value = inOneDay(); minutes.value = 65; region.value = 'uk';
    portMode.value = 'random'; chosenPort.value = null;
    generateRandomCredentials();
    loadingPorts.value = true;
    const [clients, free] = await Promise.all([store.getClientList(), store.getAvailablePorts()]);
    customerList.value = clients || [];
    ports.value = free || [];
    loadingPorts.value = false;
}
onMounted(() => modalEl.value && modalEl.value.addEventListener('shown.bs.modal', refresh));
onBeforeUnmount(() => modalEl.value && modalEl.value.removeEventListener('shown.bs.modal', refresh));
watch(() => props.customerEmail, (v) => { if (v && v !== 'All' && !customerEmail.value) customerEmail.value = v; });

async function create() {
    const parts = String(authUser.value || '').split(':');
    if (parts.length < 2 || parts[0].length < 4 || parts[1].length < 4) {
        useAlertStore().error('Enter username:password, at least 4 characters each'); return;
    }
    const server = selectedServer.value;
    if (portMode.value === 'choose' && !chosenPort.value) { useAlertStore().error('Choose a port, or switch back to Random'); return; }
    if (portMode.value === 'random' && !server.free.length) { useAlertStore().error('No connected free port on this server; choose one from the list'); return; }
    const chosen = portMode.value === 'choose' ? chosenPort.value : server.free[Math.floor(Math.random() * server.free.length)];   // the admin's pick, or a random connected free port
    busy.value = true;
    const ok = await store.quickCreateLicense({
        customer_email: String(customerEmail.value || '').trim(),
        modem_name: server.modem_name,
        ports: chosen,
        amount: amount.value,
        unit: unit.value,
        expired_date: isCustom.value ? new Date(customDate.value).toISOString() : undefined,
        auth_user: authUser.value.trim(),
        supplier_amount: (!isTest.value && supplier.value && !noSupplierCredit.value) ? Math.max(0, Number(supplierAmount.value) || 0) : 0,   // tests never credit a supplier
        no_supplier_credit: !isTest.value && !!supplier.value && noSupplierCredit.value,
        customer_amount: chargeCustomer.value ? Math.max(0, Number(customerAmount.value) || 0) : 0,
    });
    busy.value = false;
    if (ok) {
        if (ok.charged) useAlertStore().success('$' + money(ok.charged.amount) + ' taken from ' + ok.charged.email + ' (balance $' + money(ok.charged.balance) + ')');
        if (ok.manual_error) useAlertStore().error('Licence added, but the transaction entry failed: ' + ok.manual_error);
        if (ok.credited) useAlertStore().success('$' + money(ok.credited.amount) + ' booked to ' + ok.credited.name + ' (balance $' + money(ok.credited.balance) + ')');
        if (ok.supplier_error) useAlertStore().error('Licence added, but the supplier top-up failed: ' + ok.supplier_error);
        if (supplier.value && ok.credited) supplier.value = { ...supplier.value, balance: ok.credited.balance };
        emit('updated');
        // the port just used is gone; drop the server from the list if it was its last one
        ports.value = ports.value.filter(p => !(p.modem_name === server.modem_name && p.ports === chosen));
        chosenPort.value = null;
        if (!servers.value.find(s => s.modem_name === server.modem_name)) selectedServer.value = null;
        else selectedServer.value = servers.value.find(s => s.modem_name === server.modem_name);
        generateRandomCredentials();
    }
}
</script>

<style scoped>
.port-pick { margin-top: 8px; border: 1px solid var(--ui-pri); border-radius: 8px; overflow: hidden; }
.port-pick__row { display: grid; grid-template-columns: 1.4fr 1.2fr 1fr 0.9fr; gap: 8px; align-items: center; margin: 0; padding: 7px 12px; border-top: 1px solid var(--ui-line); font-size: 13px; cursor: pointer; }
.port-pick__row--head { border-top: 0; background: var(--ui-card-2); color: var(--ui-dim); font-size: 11.5px; cursor: default; }
.port-pick__row.is-on { background: var(--ui-pri-soft); }
.text-ok { color: var(--ui-ok); } .text-warn { color: var(--ui-warn); }
</style>
